import React, { useEffect, useRef, useState } from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { FiMenu, FiX } from "react-icons/fi";
// Ensure splogo path is correct in your project
import { splogo } from "../assets/index";
import { useStateContext } from "../contexts/ContextProvider";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import SelectProject from "./SelectProject";

// Material UI Imports for Notifications
import {
  IconButton,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import { Notifications } from "@mui/icons-material";

const Navbar = ({ onLogout }) => {
  const { setActiveMenu, activeMenu, setScreenSize, screenSize } =
    useStateContext();
  const navigate = useNavigate();
  const location = useLocation(); // To check current URL
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef(null);

  const [userData, setUserData] = useState([]);
  const [uniqueUserID, setUniqueUserID] = useState(
    localStorage.getItem("uniqueUserID"),
  );
  const [projectName, setProjectName] = useState(
    localStorage.getItem("project_name"),
  );

  // Notifications State
  const [invitations, setInvitations] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [refresh, setRefresh] = useState(Math.random());

  const email = localStorage.getItem("email");

  // ✅ Handle screen resizing
  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setActiveMenu(screenSize > 900);
  }, [screenSize]);

  // ✅ Fetch user data
  const url = "https://api.confidanto.com/header-data";
  const fetchInfo = async () => {
    try {
      const res = await axios.post(url, {
        email: localStorage.getItem("email"),
      });
      setUserData([res.data.userData]);
      localStorage.setItem("uniqueUserID", res.data.userData.unique_user_id);
      setUniqueUserID(res.data.userData.unique_user_id);
    } catch (error) {
      console.log(error.response);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  // ✅ Fetch Invitations logic
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!email) return;
      try {
        const response = await axios.post(
          "https://api.confidanto.com/send-invitation-email/fetch-received-invites",
          { email: email },
        );

        let fetchedInvites = [];
        if (Array.isArray(response.data)) {
          fetchedInvites = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          fetchedInvites = response.data.data;
        }

        // Filter out accepted ones based on "invitation_status"
        const pendingInvites = fetchedInvites.filter(
          (inv) => inv.invitation_status?.toLowerCase() !== "accepted",
        );

        setInvitations(pendingInvites);
      } catch (error) {
        console.error("Error fetching invitations:", error);
      }
    };

    fetchInvitations();
  }, [email, refresh]); // Triggered on initial load and when 'refresh' is updated

  const handleAcceptInvitation = async (invProjectId) => {
    setAcceptingId(invProjectId);
    try {
      await axios.post(
        "https://api.confidanto.com/send-invitation-email/accept-invitation",
        {
          project_id: invProjectId,
          invitedEmail: email,
        },
      );
      // Refresh the page data after accepting the invitation
      setRefresh(Math.random());

      // Optional: If you want the project list page to update too, dispatch a custom event
      window.dispatchEvent(new Event("projectListRefreshNeeded"));
      window.location.reload();
    } catch (error) {
      console.error("Error accepting invitation:", error);
    } finally {
      setAcceptingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setShowProfile(false);
    onLogout();
    navigate("/");
  };

  // ✅ Close profile when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Handle project name change in localStorage instantly
  useEffect(() => {
    const handleLocalStorageChange = (event) => {
      if (!event.detail || event.detail.key === "project_name") {
        setProjectName(localStorage.getItem("project_name"));
      }
    };
    window.addEventListener("localStorageChange", handleLocalStorageChange);
    return () =>
      window.removeEventListener(
        "localStorageChange",
        handleLocalStorageChange,
      );
  }, []);

  return (
    <div
      id="main-navbar"
      className="w-full bg-white dark:bg-gray-800 shadow-sm top-0 left-0 z-40 fixed md:relative"
    >
      <div className="flex justify-between items-center w-full max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 py-2 sm:py-3">
        {/* ========== LEFT SECTION (Menu + Project Name) ========== */}
        <div className="flex items-center gap-3 w-1/2 sm:w-auto">
          {/* Menu Toggle for small screens */}
          <button
            type="button"
            onClick={() => setActiveMenu((prev) => !prev)}
            className="block md:hidden text-gray-700 dark:text-gray-200 text-xl p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            {activeMenu ? <FiX /> : <FiMenu />}
          </button>

          {/* Project Name */}
          {projectName && (
            <h1
              onClick={() =>
                navigate(
                  `/project-details/${localStorage.getItem("project_id")}`,
                )
              }
              className="ml-1 sm:ml-2 text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-indigo-600 cursor-pointer truncate max-w-[60vw] sm:max-w-[300px] md:max-w-[400px]"
            >
              {projectName}
            </h1>
          )}
        </div>

        {/* ========== RIGHT SECTION (Notifications + Profile) ========== */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Notifications Icon (Only show on /projects URL) */}
          {(location.pathname === "/projects" || location.pathname === "/ads-project-performance")  && (
            <Tooltip title={`${invitations.length} unaccepted invitation(s)`}>
              <IconButton
                onClick={() => setNotificationsOpen(true)}
                color="primary"
                className="bg-gray-50 hover:bg-gray-200 transition-colors"
              >
                <Badge badgeContent={invitations.length} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Profile Details */}
          <div
            onClick={() => setShowProfile(!showProfile)}
            className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          >
            {userData.map((dataObj) => (
              <div
                key="avatar"
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-indigo-600 text-white font-bold flex-shrink-0"
              >
                {dataObj.username.charAt(0, 1)}
              </div>
            ))}

            {/* Hide text on very small screens */}
            <div className="hidden sm:flex flex-col max-w-[100px] sm:max-w-[140px] md:max-w-[180px] truncate">
              {userData.map((dataObj) => (
                <div key="user-info">
                  <span className="block text-gray-800 dark:text-gray-200 font-semibold text-sm truncate">
                    {dataObj.username}
                  </span>
                  <span className="text-gray-400 text-xs flex items-center">
                    ID: {uniqueUserID}
                    {showProfile ? (
                      <MdKeyboardArrowUp className="ml-1 text-gray-500" />
                    ) : (
                      <MdKeyboardArrowDown className="ml-1 text-gray-500" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ========== PROFILE DROPDOWN ========== */}
      {showProfile && (
        <div
          ref={profileRef}
          className="absolute right-2 sm:right-4 top-14 w-[95%] sm:w-[420px] md:w-[460px] bg-white dark:bg-gray-900 shadow-xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5 rounded-xl z-50 
                     transition ease-out duration-200 transform origin-top scale-95 animate-fadeIn"
        >
          {/* Profile Info */}
          <div className="flex items-center gap-3 border-b pb-3 sm:pb-4 mb-3 sm:mb-4">
            {userData.map((dataObj) => (
              <div
                key="profile-avatar"
                className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-indigo-600 text-white font-bold text-lg flex-shrink-0"
              >
                {dataObj.username.charAt(0, 1)}
              </div>
            ))}
            <div className="truncate w-full overflow-hidden">
              {userData.map((dataObj) => (
                <div key="profile-info">
                  <p className="text-gray-900 dark:text-gray-100 font-bold text-sm sm:text-base truncate">
                    {dataObj.username}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs truncate mt-0.5">
                    {uniqueUserID}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs truncate">
                    {dataObj.email}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Select Project Section */}
          <div className="mb-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2 border border-gray-100 dark:border-gray-800">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ml-1">
              Your Projects
            </h4>
            <SelectProject onSelectProject={() => setShowProfile(false)} />
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full py-2.5 px-4 rounded-full border border-indigo-600 text-indigo-600 font-medium hover:bg-indigo-600 hover:text-white transition-colors text-sm sm:text-base"
          >
            Log Out
          </button>
        </div>
      )}

      {/* ========== Invites Notifications Dialog ========== */}
      <Dialog
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Project Invitations</DialogTitle>
        <DialogContent dividers>
          {invitations.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No unaccepted invitations.
            </p>
          ) : (
            <List>
              {invitations.map((invite, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={`Project: ${invite.project_name || invite.projectName || "Unknown Project"}`}
                      secondary={`Invited by: ${invite.project_owner_email || invite.inviteFrom || "Unknown Email"} ${invite.access_level ? `| Role: ${invite.access_level}` : ""}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={acceptingId === invite.project_id}
                        onClick={() =>
                          handleAcceptInvitation(invite.project_id)
                        }
                      >
                        {acceptingId === invite.project_id
                          ? "Accepting..."
                          : "Accept"}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < invitations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationsOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Navbar;
